/**
 * Manifest schemas
 */

import type { CommandHotkey } from '@tunasong/schemas'
import {
  agentReadCursorsSchema,
  favoriteSchemaLegacy,
  hotkeySchema,
  maturitySchema,
  profileGearSchema,
} from '@tunasong/schemas'
import { z } from 'zod'

/**
 * Default keyboard shortcuts shipped with Tunasong. Exported so the runtime
 * can forward-merge new defaults onto existing users' persisted profiles —
 * Zod `.default(...)` only fires on a missing field, not on an existing array
 * with stale entries. See `mergeMissingHotkeys` in `@tunasong/redux`.
 */
export const defaultHotkeys: CommandHotkey[] = [
  { hotkey: 'opt+up', label: 'Block: Move Up', commandId: 'block-move-up' },
  { hotkey: 'opt+down', label: 'Block: Move Down', commandId: 'block-move-down' },
  { hotkey: 'shift+mod+d', label: 'Block: Duplicate', commandId: 'block-duplicate' },
  { hotkey: 'shift+mod+backspace', label: 'Block: Delete', commandId: 'block-delete' },
  { hotkey: 'mod+b', label: 'Format: Bold', commandId: 'format-bold' },
  { hotkey: 'mod+i', label: 'Format: Italic', commandId: 'format-italic' },
  { hotkey: 'tab', label: 'Suggestions: Insert', commandId: 'suggestions-insert' },
  { hotkey: 'mod+shift+j', label: 'Agent: Toggle Panel', commandId: 'agent-toggle-panel' },
]

/** @todo these should be in the plugins and "injected" into the manifest (e.g., during build?) */
export const midiPluginSchema = z
  .object({
    midi: z.boolean().describe('MIDI support').default(false),
    btMidi: z.boolean().describe('Bluetooth MIDI support (experimental)').default(false),
    btMidiAutoConnect: z.boolean().describe('Auto-connect to paired Bluetooth MIDI devices').default(false),
  })
  .optional()

export const chordPlayerPluginSchema = z
  .object({
    play: z.boolean().describe('Play chords on selection').default(false),
  })
  .optional()

export const chordPluginSchema = z
  .object({
    showTranspose: z.boolean().describe('Show transposition with the chord, e.g., +2').default(true),
  })
  .optional()

export const externalToolSchema = z.object({
  name: z.string(),
  url: z.string().url(),
})

export type ExternalTool = z.infer<typeof externalToolSchema>

/**
 * Resolve the default `featureMaturity` lazily so the Vitest setup can opt every
 * test into `'labs'` via `TUNA_DEFAULT_FEATURE_MATURITY=labs`, exposing plugins
 * gated at `beta`/`labs` (e.g. plugin-agent) to the CI test surface. Production
 * code never sets this env var, so consumers continue to default to `'stable'`.
 *
 * Context: `plugin-agent` shipped at `maturity: 'beta'` and `createTunaEditor`
 * silently filtered it out for users on default `'stable'` settings. The bug
 * went undetected because Vitest also defaulted to `'stable'`, so no test
 * exercised the same pluginList real users saw. See
 * `apps/tunasong/app/src/di/primary-surface-maturity.test.ts` for the explicit
 * regression guard, and `infra/base-config/vitest.setup.js` for where the env
 * var is set.
 */
const resolveDefaultFeatureMaturity = (): 'labs' | 'beta' | 'stable' => {
  // Read `process.env` without depending on `@types/node` (manifest doesn't bring it in).
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
  const fromEnv = env?.TUNA_DEFAULT_FEATURE_MATURITY
  if (fromEnv === 'labs' || fromEnv === 'beta' || fromEnv === 'stable') {
    return fromEnv
  }
  return 'stable'
}

export const userConfigSchema = z.object({
  featureMaturity: maturitySchema.optional().default(resolveDefaultFeatureMaturity),
  theme: z.enum(['light', 'dark', 'system']).optional().default('system'),
  /** Favorites - entity IDs or page path (e.g., /app/guitarpatterns) of the user's favorites */
  favorites: z.array(favoriteSchemaLegacy).optional(),
  /** AI Configuration */
  ai: z.record(z.string(), z.unknown()).optional(),
  /** The keyboard shortcuts / hotkeys for the user. Format handled by the useHotkeys hook */
  hotkeys: z.array(hotkeySchema).default(defaultHotkeys),
  musicPreferences: z
    .object({
      transpose: z.number().optional(),
      noteFormat: z.enum(['standard', 'norwegian']).optional(), // Norwegian uses H instead of B
      /**
       * Theory-label vocabulary level — gates copy across every music-theory
       * learning surface (cadence chip, progression overlay, modal glow,
       * suggestion explainer, chord popup). When absent, consumers fall back
       * to 'intermediate' at the use site (no schema-level auto-migration).
       *
       * See docs/superpowers/specs/2026-05-16-music-theory-learning-phase-1-design.md.
       */
      theoryLevel: z
        .enum(['beginner', 'intermediate', 'advanced', 'off'])
        .describe('Theory-label vocabulary level — gates copy across all music-theory learning surfaces.')
        .optional(),
      /**
       * Default mode for new Melody Writer sessions. `guided` enables
       * layer-by-layer pedagogical scaffolding (chord tones first,
       * passing/neighbor next, etc.); `free-play` exposes all affordances
       * at once. Each melody-line stores its own `mode` field, so changing
       * this default does not retroactively re-mode existing melodies.
       *
       * See docs/superpowers/specs/2026-05-17-melody-writer-design.md.
       */
      melodyWriterMode: z
        .enum(['guided', 'free-play'])
        .describe('Default mode for new Melody Writer sessions.')
        .optional(),
      /**
       * Opt-in toggle for the in-editor voice-leading diff (the inter-chord
       * connector showing common tones held / steps / leaps). Off by default
       * even when `theoryLevel` is on: it is the densest per-pair surface, so
       * it requires its own explicit gate (Joni visual spec §4.2). Kept
       * `.optional()` (not `.default(false)`) to mirror `theoryLevel`: the
       * absence is resolved to `false` at the use site (`useShowVoiceMotion`),
       * so existing persisted configs need no migration and existing
       * `musicPreferences` literals stay valid.
       *
       * See docs/superpowers/specs/2026-06-06-voice-leading-diff-visual-spec.md.
       */
      showVoiceMotion: z
        .boolean()
        .describe('Opt-in: show the in-editor voice-leading diff between adjacent chords.')
        .optional(),
    })
    .optional(),
  /** Gear requirements for technical riders */
  gear: profileGearSchema.optional(),
  /** External tools - user-configured links to external services */
  externalTools: z.array(externalToolSchema).optional().default([]),
  /**
   * Per-user, per-agent "last read" timestamps. Drives the Agent Rail's
   * unread-dot derivation. Cross-device synced via profile push.
   */
  agentReadCursors: agentReadCursorsSchema.optional(),
  plugins: z
    .lazy(() =>
      z
        .object({
          // The key is the plugin key / id
          midi: midiPluginSchema,
          'chord-player': chordPlayerPluginSchema,
          chord: chordPluginSchema,
        })
        .loose()
    )
    .optional()
    .default({}),
})
